import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, createSearchParams } from 'react-router-dom'
import { NavContext } from '../../../context/global_context'
import { deleteRequest, getRequest, postRequest } from '../../../services/base'
import { SalesCodeComplexityContext, SET_SALES_CODE_PLUGIN_DATA, SET_SELECTED_IDS, SET_SHOW_HIDE_DETAILS_POPUP, SET_SHOW_HIDE_ERROR_LOGS, SET_SHOW_HIDE_FORM_POPUP, SET_UPLOAD_FILE_STATUS } from './context/context'
import { getSessionStorageData } from '../../../utils/SessionStorage'
import { Dropdown, Empty, Flex, Form, Modal, Segmented, Space, Table, Typography } from 'antd'
import { DeleteOutlined, DownloadOutlined, DownOutlined, ExclamationCircleOutlined, MoreOutlined, UploadOutlined, WarningOutlined } from '@ant-design/icons'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import RecordsCount from '../../common/RecordsCount'
import SalesCodeComplexityFormModal from './form'
import SalesCodeDetails from './SalesCodeDetails'
import SalesCodeErrorLogs from './error_logs/SalesCodeErrorLogs'
import { buildPageUrl, downloadErrorLogs, downloadFile, formatDateTime, formatEmptyValue, getDeleteTargetPage } from '../../../utils/common'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import { ProgresSpinner } from '../../common/ProgresSpinner'
import { useIntl } from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const SalesCodeComplexity = () => {
  const { vehicle } = useParams()
  const navigate = useNavigate()
  const intl = useIntl()
  const [modal, contextHolder] = Modal.useModal()
  const [salesCodeState, salesCodeDispatch] = useContext(SalesCodeComplexityContext)
  const [state, dispatch] = useContext(NavContext)
  const [pagination, setPagination] = useState({})
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [expandedLogs, setExpandedLogs] = useState({})
  const intervalRef = useRef(null)
  const dataIntervalRef = useRef(null)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  const currentUrl = useRef(`/api/plugin/sales_code/?vehicle=${vehicle}&search=${searchValue}`)

  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: `${intl.formatMessage({ id: 'layout.vehicleList' })} / ${vehicle} / ${intl.formatMessage({ id: 'sidebar.plugin' })} / ${intl.formatMessage({ id: 'sidebar.salesCodeComplexity' })}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })

    getSalesCodeComplexityData('', true)
    fetchComplexityStatusInterval()

    intervalRef.current = setInterval(fetchComplexityStatusInterval, 10000)
    dataIntervalRef.current = setInterval(() => getSalesCodeComplexityData(), 30000)

    return () => {
      clearInterval(intervalRef.current)
      clearInterval(dataIntervalRef.current)
    }
  }, [])

  const fetchComplexityStatusInterval = async () => {
    let res = await getRequest(`/api/plugin/sales_code/upload_status_check/${vehicle}/?vehicle=${vehicle}`)
    if (res?.status) {
      salesCodeDispatch({ type: SET_UPLOAD_FILE_STATUS, data: { ...res.data } })
    }
  }

  const getSalesCodeComplexityData = async (url = null, showLoader = false) => {
    if (showLoader) setLoading(true)

    let response = await getRequest(currentUrl.current)
    salesCodeDispatch({ type: SET_SALES_CODE_PLUGIN_DATA, data: response.results })
    if (response?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'salesCodeComplexity.loadError' }))
    }
    setPagination(response.pagination)
    if (showLoader) setLoading(false)
  }

  const downloadSalesCodeFile = async (download_option = null) => {
    const downloadOptionLabel =
      download_option === 'All'
        ? intl.formatMessage({ id: 'generic.all' })
        : intl.formatMessage({ id: 'forms.selected' })

    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'salesCodeComplexity.downloadConfirmationTitle' }),
      content: intl.formatMessage(
        { id: 'salesCodeComplexity.downloadConfirmationContent' },
        { downloadOption: downloadOptionLabel }
      ),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.download' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        dispatch({
          type: 'loader',
          data: { caption: intl.formatMessage({ id: 'salesCodeComplexity.downloading' }), showLoader: true },
          notification: {},
        })

        let formData = new FormData()
        if (download_option === 'All') {
          formData.append('sales_code', 'All')
        } else {
          salesCodeState.selectedIds.forEach((element) => {
            formData.append('sales_code', element)
          })
        }
        formData.append('vehicle', vehicle)

        let res = await postRequest(`/api/plugin/sales_code/bulk/download/`, formData, true)
        // Was `if (res?.msg)`: a failure carrying no message fell through and
        // saved the error object as the file. Same guard as the other exports.
        if (res?.status === false || !(res instanceof Blob)) {
          dispatch({
            type: 'notification',
            data: { notification_type: 'error', msg: intl.formatMessage({ id: 'salesCodeComplexity.downloadError' }), loader: true },
          })
        } else {
          const url = window.URL.createObjectURL(new Blob([res]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${vehicle}_sales_code_complexity.csv`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          dispatch({
            type: 'notification',
            data: { notification_type: 'success', msg: intl.formatMessage({ id: 'salesCodeComplexity.fileDownloaded' }), loader: true },
          })
        }
      },
    })
  }

  const deleteSalesCodeComplexity = async (sales_code_id) => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'salesCodeComplexity.deleteConfirmationTitle' }),
      content: intl.formatMessage({ id: 'salesCodeComplexity.deleteConfirmationContent' }),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        let res = await deleteRequest(`/api/plugin/sales_code/${sales_code_id}/?vehicle=${vehicle}`)
        currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
        getSalesCodeComplexityData(null, true)
        dispatch({
          type: 'notification',
          data: { notification_type: 'success', msg: intl.formatMessage({ id: 'salesCodeComplexity.deleted' }) },
        })
      },
    })
  }

  const toggleExpand = async (id) => {
    const isExpanding = !expandedRowKeys.includes(id)
    setExpandedRowKeys((prev) =>
      prev.includes(id) ? prev.filter((key) => key !== id) : [id]
    )
    if (isExpanding) {
      const res = await getRequest(`/api/plugin/sales_code/sales-code-details/${id}/?vehicle=${vehicle}`)
      if (res?.status === false) {
        notifyError(dispatch, intl.formatMessage({ id: 'salesCodeComplexity.detailsLoadError' }))
        return
      }
      setExpandedLogs((prev) => ({
        ...prev,
        [id]: {
          error: res?.data?.logs?.error?.length || 0,
          success: res?.data?.logs?.success?.length || 0,
        },
      }))
    }
  }

  const downloadLogs = async (id, logType) => {
    const res = await getRequest(`/api/plugin/sales_code/sales-code-details/${id}/?vehicle=${vehicle}`)
    // Clicking "download logs" used to do nothing at all when the request failed.
    if (res?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'salesCodeComplexity.logsDownloadError' }))
      return
    }
    if (res?.data?.logs?.[logType]?.length > 0) {
      downloadErrorLogs(res.data.logs[logType], `${logType}_${id}.txt`)
    }
  }

  const locationPath = `/vehicles/${vehicle}/plugins/sales_code_complexity`
  const menuItems = useMemo(() => [
    { label: intl.formatMessage({ id: 'sidebar.salesCodeComplexity' }), value: `/vehicles/${vehicle}/plugins/sales_code_complexity` },
    { label: intl.formatMessage({ id: 'sidebar.defPlugin' }), value: `/vehicles/${vehicle}/plugins/def` },
    { label: intl.formatMessage({ id: 'sidebar.capitalHarness' }), value: `/vehicles/${vehicle}/plugins/capital_harness` },
  ], [vehicle, intl])

  const dataSource = useMemo(() => (salesCodeState?.data || []).map((item, index) => ({
    key: item.id || index,
    id: item.id,
    harness: item.harness,
    harness_type: item.harness_type,
    updated_at: item.updated_at,
    file_names: item.file_names,
    file: item.file,
    name: item.name,
    has_logs: item.has_logs,
  })), [salesCodeState?.data])

  const columns = useMemo(() => [
    {
      title: intl.formatMessage({ id: 'generic.harness' }),
      dataIndex: 'harness',
      sorter: (a, b) => (a.harness || '').localeCompare(b.harness || ''),
      ellipsis: { showTitle: false },
      render: (text) => <EllipsisTooltip className='ellipsis'>{formatEmptyValue(text)}</EllipsisTooltip>,
    },
    {
      title: (
        <EllipsisTooltip className="ellipsis">
          {intl.formatMessage({ id: 'salesCodeComplexity.harnessType' })}
        </EllipsisTooltip>
      ),
      dataIndex: 'harness_type',
      sorter: (a, b) => (a.harness_type || '').localeCompare(b.harness_type || ''),
      ellipsis: { showTitle: false },
      render: (text) => <EllipsisTooltip className='ellipsis'>{formatEmptyValue(text)}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'generic.date' }),
      dataIndex: 'updated_at',
      width: '20%',
      sorter: (a, b) => (a.updated_at || '').localeCompare(b.updated_at || ''),
      defaultSortOrder: 'descend',
      render: (text) => <EllipsisTooltip className='ellipsis'>{formatDateTime(text)}</EllipsisTooltip>,
    },
    {
      title: '',
      align: 'right',
      width: '260px',
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.key)
        return (
          <Space size="small">
            {record.has_logs && (
              <Button
                type="link"
                className={`${isExpanded ? 'link-button neutral' : 'link-button'}`}
                style={{padding: 0, gap: 6}}
                onClick={() => toggleExpand(record.key)}
              >
                {isExpanded
                  ? intl.formatMessage({ id: 'salesCodeComplexity.hideLogs' })
                  : intl.formatMessage({ id: 'salesCodeComplexity.viewLogs' })}
                <DownOutlined style={{ fontSize: 10 }} rotate={isExpanded ? 180 : 0} />
              </Button>
            )}

            <Button
              type="link"
              className="link-button"
              onClick={() => {
                salesCodeDispatch({ type: SET_SHOW_HIDE_DETAILS_POPUP, data: record })
              }}
            >
              {intl.formatMessage({ id: 'salesCodeComplexity.viewFiles' })}
            </Button>
            
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              overlayStyle={{ marginTop: 12 }}
              menu={{
                items: [
                  {
                    key: 'delete',
                    label: intl.formatMessage({ id: 'forms.delete' }),
                    icon: <DeleteOutlined style={{ color: 'var(--color-form-link-danger)' }} />,
                    danger: true,
                  },
                ],
                onClick: ({ key }) => {
                  if (key === 'delete') deleteSalesCodeComplexity(record.id)
                },
              }}
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        )
      },
    },
  ], [expandedRowKeys, expandedLogs, intl])

  const selectedRowKeys = salesCodeState?.selectedIds || []

  const rowSelection = {
    selectedRowKeys,
    hideSelectAll: true,
    onSelect: (record, selected) => {
      salesCodeDispatch({ type: SET_SELECTED_IDS, data: record.id })
    },
  }

  const downloadMenuItems = useMemo(() => [
    {
      key: 'all',
      label: intl.formatMessage({ id: 'salesCodeComplexity.downloadAllFiles' }),
      onClick: () => downloadSalesCodeFile('All'),
    },
    {
      key: 'selected',
      label: intl.formatMessage({ id: 'salesCodeComplexity.downloadSelectedFiles' }),
      disabled: (salesCodeState?.selectedIds?.length || 0) === 0,
      onClick: () => downloadSalesCodeFile('selected'),
    },
  ], [salesCodeState?.selectedIds, intl])

  const { currentPage, totalCount } = pagination

  return (
    <div className="page sales-code-complexity">
      <Flex className="title">
        <Segmented
          options={menuItems}
          value={locationPath}
          onChange={(value) => navigate(value)}
        />
      </Flex>

      <Flex vertical className="body">
        <Flex align="center" justify="space-between" gap={16} wrap>
          <Flex align="center" gap={8} style={{ flex: 1 }}>
            <Form style={{width: '100%'}}>
              <InputText
                name="search_value"
                placeholder={intl.formatMessage({ id: 'salesCodeComplexity.searchPlaceholder' })}
                value={searchValue}
                type="search"
                onChange={(value) => setSearchValue(value)}
                isRequired={false}
                wrapperClass="search-wrapper"
                onSubmit={() => {
                  currentUrl.current = `/api/plugin/sales_code/?vehicle=${vehicle}&search=${searchValue}`
                  getSalesCodeComplexityData('', true)
                }}
              />
            </Form>
          </Flex>

          <Space size="middle">
            {salesCodeState?.in_process_upload_files && (
              <ProgresSpinner caption={intl.formatMessage({ id: 'salesCodeComplexity.fileUploadInProgress' })} className="upload-inprogress" size="medium" />
            )}

            <Button
              icon={<WarningOutlined />}
              onClick={() => salesCodeDispatch({ type: SET_SHOW_HIDE_ERROR_LOGS, data: true })}
            >
              {intl.formatMessage({ id: 'salesCodeComplexity.errorLogs' })}
            </Button>

            <Dropdown menu={{ items: downloadMenuItems }} trigger={['click']}>
              <Button icon={<DownloadOutlined />}>
                {intl.formatMessage({ id: 'forms.download' })}
              </Button>
            </Dropdown>

            <Button
              type="primary"
              onClick={() => salesCodeDispatch({ type: SET_SHOW_HIDE_FORM_POPUP, data: true })}
            >
              {intl.formatMessage({ id: 'salesCodeComplexity.uploadFiles' })}
            </Button>
          </Space>
        </Flex>

        <Table
          rowKey="key"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          showSorterTooltip={false}
          expandable={{
            expandIcon: () => null,
            showExpandColumn: false,
            expandedRowKeys,
            expandedRowRender: (record) => {
              return (
                <Flex justify='space-between' className='expanded-row' gap={8}>
                  <Space size={'middle'}>
                    <Typography.Text strong>{intl.formatMessage({ id: 'salesCodeComplexity.uploadLogs' })}</Typography.Text>

                    {record.file_names?.length > 0 && (
                      <Flex vertical>
                        {record.file_names.map((file, i) => (
                          <Typography.Text key={i}>
                            {file}
                          </Typography.Text>
                        ))}
                      </Flex>
                    )}
                  </Space>

                  <Space size={'middle'}>
                    {expandedLogs[record.id]?.error > 0 && (
                      <Button
                        type="link"
                        className="link-button"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadLogs(record.id, 'error')}
                      >
                        {intl.formatMessage({ id: 'salesCodeComplexity.downloadErrorLogs' })}
                      </Button>
                    )}
                    
                    {expandedLogs[record.id]?.success > 0 && (
                      <Button
                        type="link"
                        className="link-button"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadLogs(record.id, 'success')}
                      >
                        {intl.formatMessage({ id: 'salesCodeComplexity.downloadSuccessLogs' })}
                      </Button>
                    )}
                  </Space>
                </Flex>
              )
            },
            rowExpandable: () => true,
          }}
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            total: totalCount,
            pageSize: 10,
            showSizeChanger: false,
            hideOnSinglePage: true,
            showTotal: (total) => <RecordsCount count={total} />,
            onChange: (page) => {
              const offset = (page - 1) * 10
              const url = pagination?.nextPageLink ? new URL(pagination.nextPageLink) : new URL(pagination?.prevPageLink)
              url.searchParams.set('offset', offset)
              currentUrl.current = url.toString()
              getSalesCodeComplexityData('', true)
            },
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px' }}>
                <Empty description={<RecordsCount count={0} />} />
              </div>
            ),
          }}
        />
        {pagination &&
        pagination?.totalPages <= 1 &&
        pagination?.totalCount > 0 && (
          <Typography.Text>
            <RecordsCount count={pagination?.totalCount} />
          </Typography.Text>
        )}
      </Flex>

      <SalesCodeComplexityFormModal getSalesCodeComplexityData={getSalesCodeComplexityData} />
      <SalesCodeDetails />
      <SalesCodeErrorLogs />
      {contextHolder}
    </div>
  )
}

export default SalesCodeComplexity
